import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CHAPTERS, IMAGES } from "@/lib/siteData";

const Chapter = ({ ch, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const flip = index % 2 === 1;

  return (
    <div
      ref={ref}
      className={`grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-20 ${flip ? "" : ""}`}
      data-testid={`chapter-${ch.no}`}
    >
      {/* Image */}
      <div className={`relative ${flip ? "lg:order-2" : "lg:order-1"}`}>
        <div className="relative overflow-hidden rounded-[2.5rem] rounded-tr-[6rem]">
          <motion.img
            style={{ y }}
            src={IMAGES[ch.image]}
            alt={ch.title}
            className="aspect-[4/5] w-full scale-110 object-cover"
          />
          <div className="absolute inset-0 ring-1 ring-inset ring-ink/10 rounded-[2.5rem] rounded-tr-[6rem]" />
        </div>
      </div>

      {/* Text */}
      <div className={`relative ${flip ? "lg:order-1" : "lg:order-2"}`}>
        <span className="pointer-events-none absolute -top-24 -left-4 select-none font-display text-[9rem] lg:text-[13rem] leading-none text-ink/[0.06]">
          {ch.no}
        </span>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <p className="mb-4 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-magenta">
            <span className="h-px w-10 bg-magenta" /> Kapitel {ch.no}
          </p>
          <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink leading-tight">
            {ch.title}
          </h3>
          <p className="mt-6 max-w-lg text-base sm:text-lg leading-relaxed text-ink/60">
            {ch.text}
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export const Manifesto = () => {
  return (
    <section id="wirkung" className="relative bg-ivory px-4 sm:px-6 py-28 sm:py-36" data-testid="manifesto-section">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-24 max-w-3xl"
        >
          <p className="mb-5 text-xs uppercase tracking-[0.35em] text-violet">Ihre Unterstützung macht den Unterschied</p>
          <h2 className="font-display text-4xl sm:text-6xl tracking-tight text-ink leading-[1.02]">
            Jeder Franken schenkt kranken Kindern neue <span className="italic text-magenta">Chancen</span>.
          </h2>
        </motion.div>

        <div className="space-y-28 sm:space-y-40">
          {CHAPTERS.map((ch, i) => (
            <Chapter key={ch.no} ch={ch} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
